"use client";

import { useCallback, useState } from "react";
import type { Item } from "@/lib/types";

/**
 * Single owner of the item quick-view (preview modal) state.
 *
 * A card click opens the modal with the clicked item for instant paint; the
 * modal then fetches full details by id. Used anywhere items are listed
 * (homepage shelves, market grid) so the open/close behavior is not
 * reimplemented per surface.
 */
export function useItemPreview() {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  const openPreview = useCallback((item: Item) => {
    setSelectedItem(item);
    setSelectedItemId(item.id);
  }, []);

  const closePreview = useCallback(() => {
    setSelectedItemId(null);
    setSelectedItem(null);
  }, []);

  return {
    /** Non-null when the preview modal is open. */
    selectedItemId,
    /** Clicked item used as the modal's initial data (instant paint). */
    selectedItem,
    openPreview,
    closePreview,
  };
}

export type UseItemPreviewResult = ReturnType<typeof useItemPreview>;
